import notification from "antd/es/notification";
import { defineMessage } from "react-intl";
import { all, call, put, select, takeEvery, takeLatest } from "redux-saga/effects";
import { v4 } from "uuid";

import { GeocodeResult } from "@mapmycustomers/shared/types/base/Located";
import { Company, EntityType, Person } from "@mapmycustomers/shared/types/entity";
import { DuplicateEntity } from "@mapmycustomers/shared/types/entity/Duplicate";
import File, { RawFile } from "@mapmycustomers/shared/types/File";
import Organization from "@mapmycustomers/shared/types/Organization";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";
import {
  PlatformCombinedCondition,
  PlatformFilterCondition,
} from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformFilterModel";

import i18nService from "@app/config/I18nService";
import { CreatePersonPayload, FieldValidationErrors } from "@app/store/api/ApiService";
import { callApi } from "@app/store/api/callApi";
import { showEntityView } from "@app/store/entityView/actions";
import { showEntityChannel } from "@app/store/entityView/sagas";
import { handleError } from "@app/store/errors/actions";
import { getOrganization, getOrganizationId } from "@app/store/iam";
import { notifyAboutChanges } from "@app/store/uiSync/actions";
import FileListItem from "@app/types/FileListItem";
import { DUPLICATE_SEARCH_RADIUS, NOTIFICATION_DURATION_WITH_ACTION } from "@app/util/consts";
import { allSettled, SettleResult } from "@app/util/effects";

import getCreationGeocodeErrorMessage from "../../util/getCreationGeocodeErrorMessage";
import getSuccessNotificationNode from "../../util/getSuccessNotificationNode";

import { getFileGroupId, getUploadedFileListIds } from ".";
import {
  clearAllUploadedPersonFiles,
  createPerson,
  initialize,
  removePersonFile,
  uploadPersonFiles,
} from "./actions";

const successMessage = defineMessage({
  id: "createPersonModal.success",
  defaultMessage: "Person added successfully",
  description: "Person add success message",
});

export function* onInitialize({ payload }: ReturnType<typeof initialize.request>) {
  try {
    const fileGroupId = v4();
    const { fixedCompany } = payload;
    if (!fixedCompany) {
      yield put(initialize.success({ fileGroupId }));
      return;
    }

    // we need full company details because user may want to use "inherit address" feature
    const org: Organization = yield select(getOrganization);
    const company: Company = yield callApi("fetchCompany", org.id, fixedCompany.id);

    yield put(initialize.success({ fileGroupId, parentCompany: company }));
  } catch (error) {
    yield put(initialize.failure());
    yield put(handleError({ error }));
  }
}

export function* onCreatePerson({ payload }: ReturnType<typeof createPerson.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);

    const {
      callback,
      checkDuplicates,
      customFieldsValues,
      fieldsValueValidateCallback,
      groupsIdsToAdd,
      layoutId,
      person,
    } = payload;

    if (fieldsValueValidateCallback) {
      // Validate fields values
      let fieldErrors: FieldValidationErrors[] = [];

      const standardFieldsValidationErrors: FieldValidationErrors[] = yield callApi(
        "validateStandardFields",
        orgId,
        layoutId,
        EntityType.PERSON,
        person
      );
      if (standardFieldsValidationErrors.length) {
        fieldErrors = standardFieldsValidationErrors;
      }

      if (customFieldsValues.length) {
        const customFieldValidateResponse: FieldValidationErrors[] = yield callApi(
          "validateCustomFieldsValues",
          orgId,
          layoutId,
          EntityType.PERSON,
          undefined,
          customFieldsValues
        );

        if (customFieldValidateResponse.length) {
          fieldErrors = [...fieldErrors, ...customFieldValidateResponse];
        }
      }

      if (fieldErrors.length) {
        yield call(fieldsValueValidateCallback, fieldErrors);
        yield put(createPerson.failure({}));
        return;
      }
    }

    if (checkDuplicates) {
      const { email, firstName, geoPoint, lastName, phone } = person;
      const nameAddressCondition: PlatformCombinedCondition = {
        $and: [],
      };
      if (firstName || lastName) {
        nameAddressCondition.$and!.push({
          name: {
            $eq: [firstName, lastName].filter((name) => name?.trim().length).join(" "),
            $fuzzy: true,
          },
        });
      }
      const fieldsFilter: PlatformFilterCondition[] = [
        ...((phone ?? "").trim().length ? [{ phone }] : []),
        ...((email ?? "").trim().length ? [{ email }] : []),
      ];
      if (geoPoint) {
        nameAddressCondition.$and!.push({
          area: { point: geoPoint.coordinates, radius: DUPLICATE_SEARCH_RADIUS, uom: "metres" },
        });
      } else {
        const addressToCheck = {
          address: person.address ?? "",
          city: person.city ?? "",
          country: person.country ?? "",
          postalCode: person.postalCode ?? "",
          region: person.region ?? "",
          regionCode: person.regionCode ?? "",
        };
        if (Object.values(addressToCheck).some((value) => value.trim().length > 0)) {
          const response: GeocodeResult = yield callApi("geocodeAddress", orgId, addressToCheck);
          if (response.geoPoint) {
            nameAddressCondition.$and!.push({
              area: {
                point: response.geoPoint.coordinates,
                radius: DUPLICATE_SEARCH_RADIUS,
                uom: "metres",
              },
            });
          }
        }
      }
      if (nameAddressCondition.$and!.length) {
        fieldsFilter.push(nameAddressCondition);
      }

      if (fieldsFilter.length) {
        const response: ListResponse<DuplicateEntity> = yield callApi(
          "fetchEntityDuplicates",
          orgId,
          EntityType.PERSON,
          {
            $filters: { $or: fieldsFilter, dupeCheck: true },
          }
        );
        if (response.data.length) {
          yield put(createPerson.failure({ duplicates: response.data }));
          return;
        }
      }
    }

    const fileIds: File["id"][] = yield select(getUploadedFileListIds);

    const personToSend: CreatePersonPayload = {
      ...person,
      files: fileIds.length > 0 ? [...fileIds] : undefined,
    };
    const newPerson: Person = yield callApi("createPerson", orgId, layoutId, personToSend);

    const peopleIds: Array<Person["id"]> = [newPerson.id];
    yield all([
      ...groupsIdsToAdd.map((groupId) =>
        callApi("addToGroup", orgId, groupId, EntityType.PERSON, peopleIds)
      ),
    ]);

    if (customFieldsValues.length) {
      yield callApi(
        "createCustomFieldsValues",
        orgId,
        layoutId,
        EntityType.PERSON,
        newPerson.id,
        customFieldsValues
      );
    }

    const intl = i18nService.getIntl();
    const message = getCreationGeocodeErrorMessage(newPerson.geoStatus, intl);
    if (message) {
      notification.warning({ message });
    }

    callback?.(newPerson);
    yield put(notifyAboutChanges({ added: [newPerson], entityType: EntityType.PERSON }));
    const notificationKey = "create-person-notification";

    notification.success({
      duration: NOTIFICATION_DURATION_WITH_ACTION,
      key: notificationKey,
      message: getSuccessNotificationNode(
        i18nService.getIntl(),
        i18nService.formatMessage(successMessage, "Person added successfully"),
        payload.showNotificationViewButton
          ? () => {
              showEntityChannel.put(
                showEntityView({ entityId: newPerson.id, entityType: EntityType.PERSON })
              );
              notification.close(notificationKey);
            }
          : undefined
      ),
    });

    yield put(clearAllUploadedPersonFiles());
    yield put(createPerson.success(newPerson));
  } catch (error) {
    yield put(createPerson.failure({}));
    yield put(handleError({ error }));
  }
}

export function* onUploadPersonFiles({ payload }: ReturnType<typeof uploadPersonFiles.request>) {
  try {
    const org: Organization = yield select(getOrganization);
    const ownFileGroupId: string = yield select(getFileGroupId);
    // Empty until this modal's own initialize ran, which is exactly the case when another surface
    // (the chat proposal editor) is borrowing the uploader — so a supplied id wins.
    const fileGroupId = payload.fileGroupId ?? ownFileGroupId;
    const responses: SettleResult<RawFile>[] = yield allSettled(
      payload.files.map((file) =>
        callApi("createFile", org.id, file, undefined, undefined, undefined, {
          headers: {
            "x-mmc-file-group-id": fileGroupId,
          },
        })
      )
    );
    const fileList: FileListItem[] = responses.map((response, index) => ({
      file: payload.files[index],
      uploading: false,
      ...(response.error
        ? { errored: true, errorMessage: String(response.result) }
        : { errored: false, uploadedFile: response.result }),
    }));
    payload.callback?.(fileList);
    yield put(uploadPersonFiles.success(fileList));
  } catch (error) {
    payload.callback?.(payload.files.map((file) => ({ errored: true, file, uploading: false })));
    yield put(uploadPersonFiles.failure());
    yield put(handleError({ error }));
  }
}

export function* onRemovePersonFile({ payload }: ReturnType<typeof removePersonFile.request>) {
  try {
    const org: Organization = yield select(getOrganization);
    yield callApi("deleteFile", org.id, payload.id);
    yield put(removePersonFile.success(payload.id));
  } catch (error) {
    yield put(removePersonFile.failure());
    yield put(handleError({ error }));
  }
}

export function* createPersonModalSaga() {
  yield takeLatest(initialize.request, onInitialize);
  yield takeLatest(createPerson.request, onCreatePerson);
  yield takeEvery(uploadPersonFiles.request, onUploadPersonFiles);
  yield takeEvery(removePersonFile.request, onRemovePersonFile);
}
